import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// N8N webhook URL — set in .env as VITE_N8N_WEBHOOK_URL
const N8N_WEBHOOK_URL = import.meta.env?.VITE_N8N_WEBHOOK_URL || '';

export const vehicleHistoryService = {
  async getByVehicleId(vehicleId) {
    try {
      const { data, error } = await supabase?.from('vehicle_history')?.select(`
          *,
          work_order:related_order_id(id, folio_ot, estado_ot)
        `)?.eq('vehicle_id', vehicleId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching vehicle history:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch vehicle history:', error);
      return [];
    }
  },

  async create(historyData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const snakeData = toSnakeCase({
        ...historyData,
        createdBy: user?.id || null
      });

      const { data, error } = await supabase?.from('vehicle_history')?.insert(snakeData)?.select()?.single();

      if (error) throw new Error(error.message);
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create vehicle history entry:', error);
      throw error;
    }
  },

  async update(id, historyData) {
    try {
      const snakeData = toSnakeCase(historyData);
      const { data, error } = await supabase?.from('vehicle_history')?.update({ ...snakeData, updated_at: new Date()?.toISOString() })?.eq('id', id)?.select()?.single();

      if (error) throw new Error(error.message);
      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update vehicle history entry:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('vehicle_history')?.delete()?.eq('id', id);

      if (error) throw new Error(error.message);
      return true;
    } catch (error) {
      console.error('Failed to delete vehicle history entry:', error);
      throw error;
    }
  },

  /**
   * Auto-create a vehicle_history entry when a Work Order is created.
   * Prevents duplicates by checking source_id.
   */
  async autoCreateFromWorkOrder({ vehicleId, workOrderId, vehicleMileage = null }) {
    try {
      // Check for existing record with same source_id to avoid duplicates
      const { data: existing } = await supabase
        ?.from('vehicle_history')
        ?.select('id')
        ?.eq('source_id', workOrderId)
        ?.eq('source_type', 'WORK_ORDER')
        ?.eq('type', 'DONE')
        ?.maybeSingle();

      if (existing) return toCamelCase(existing);

      const { data, error } = await supabase?.from('vehicle_history')?.insert({
        vehicle_id: vehicleId,
        type: 'DONE',
        title: 'Orden de Trabajo generada',
        source_type: 'WORK_ORDER',
        source_id: workOrderId,
        is_auto_generated: true,
        service_km_at_time: vehicleMileage,
        status: 'OPEN',
      })?.select()?.single();

      if (error) {
        console.warn('[vehicleHistoryService] autoCreateFromWorkOrder error:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (err) {
      console.warn('[vehicleHistoryService] autoCreateFromWorkOrder failed:', err?.message);
      return null;
    }
  },

  /**
   * Auto-create a vehicle_history PENDING entry when a Quote with parts is generated.
   * Prevents duplicates by checking source_id.
   */
  async autoCreateFromQuote({ vehicleId, quoteId, title = 'Cotización con piezas pendientes' }) {
    try {
      const { data: existing } = await supabase
        ?.from('vehicle_history')
        ?.select('id')
        ?.eq('source_id', quoteId)
        ?.eq('source_type', 'QUOTE')
        ?.eq('type', 'PENDING')
        ?.maybeSingle();

      if (existing) return toCamelCase(existing);

      const { data, error } = await supabase?.from('vehicle_history')?.insert({
        vehicle_id: vehicleId,
        type: 'PENDING',
        title,
        source_type: 'QUOTE',
        source_id: quoteId,
        is_auto_generated: true,
        status: 'OPEN',
      })?.select()?.single();

      if (error) {
        console.warn('[vehicleHistoryService] autoCreateFromQuote error:', error?.message);
        return null;
      }
      return toCamelCase(data);
    } catch (err) {
      console.warn('[vehicleHistoryService] autoCreateFromQuote failed:', err?.message);
      return null;
    }
  },

  /**
   * Close a vehicle_history entry (set status = CLOSED) when a Work Order is closed.
   */
  async closeByWorkOrderId(workOrderId) {
    try {
      const { error } = await supabase
        ?.from('vehicle_history')
        ?.update({ status: 'CLOSED', updated_at: new Date()?.toISOString() })
        ?.eq('source_id', workOrderId)
        ?.eq('source_type', 'WORK_ORDER');

      if (error) console.warn('[vehicleHistoryService] closeByWorkOrderId error:', error?.message);
    } catch (err) {
      console.warn('[vehicleHistoryService] closeByWorkOrderId failed:', err?.message);
    }
  },

  /**
   * Get all active service rules.
   */
  async getServiceRules() {
    try {
      const { data, error } = await supabase
        ?.from('service_rules')
        ?.select('*')
        ?.eq('active', true)
        ?.order('service_name');

      if (error) {
        console.warn('[vehicleHistoryService] getServiceRules error:', error?.message);
        return [];
      }
      return toCamelCase(data || []);
    } catch (err) {
      console.warn('[vehicleHistoryService] getServiceRules failed:', err?.message);
      return [];
    }
  },

  /**
   * After a DONE service is recorded, apply service rules to auto-create next PENDING service.
   * Prevents duplicates by checking source_id.
   */
  async applyPreventiveRules({ vehicleId, workOrderId, serviceKmAtTime, vehicleType = null }) {
    try {
      const rules = await this.getServiceRules();
      if (!rules?.length) return;

      const today = new Date();

      for (const rule of rules) {
        // Check if rule applies to this vehicle type
        if (rule?.appliesTo && vehicleType && rule?.appliesTo !== vehicleType) continue;

        const nextKm = serviceKmAtTime && rule?.kmInterval
          ? serviceKmAtTime + rule?.kmInterval
          : null;

        const nextDate = rule?.monthsInterval
          ? (() => {
              const d = new Date(today);
              d?.setMonth(d?.getMonth() + rule?.monthsInterval);
              return d?.toISOString()?.split('T')?.[0];
            })()
          : null;

        // Avoid duplicate preventive entries for same rule + vehicle + work order
        const { data: existing } = await supabase
          ?.from('vehicle_history')
          ?.select('id')
          ?.eq('vehicle_id', vehicleId)
          ?.eq('source_id', workOrderId)
          ?.eq('title', `Próximo: ${rule?.serviceName}`)
          ?.maybeSingle();

        if (existing) continue;

        await supabase?.from('vehicle_history')?.insert({
          vehicle_id: vehicleId,
          type: 'PENDING',
          title: `Próximo: ${rule?.serviceName}`,
          notes: `Generado automáticamente por regla de mantenimiento preventivo.`,
          source_type: 'WORK_ORDER',
          source_id: workOrderId,
          is_auto_generated: true,
          service_km_at_time: serviceKmAtTime,
          next_service_km: nextKm,
          next_service_date: nextDate,
          status: 'OPEN',
          alert_ready: false,
          alert_sent: false,
        });
      }
    } catch (err) {
      console.warn('[vehicleHistoryService] applyPreventiveRules failed:', err?.message);
    }
  },

  /**
   * checkPreventiveAlerts — reviews all OPEN PENDING entries and marks alert_ready = true
   * when vehicle.mileage >= next_service_km OR today >= next_service_date - 7 days.
   * Should be called periodically (e.g., on dashboard load or scheduled).
   */
  async checkPreventiveAlerts() {
    try {
      // Fetch all OPEN PENDING entries with next_service_km or next_service_date set
      const { data: entries, error } = await supabase
        ?.from('vehicle_history')
        ?.select(`
          *,
          vehicle:vehicle_id(id, mileage, kilometraje, make, model, marca, modelo, plates, placas, client_id,
            client:client_id(id, nombre, telefono, email))
        `)
        ?.eq('type', 'PENDING')
        ?.eq('status', 'OPEN')
        ?.eq('alert_ready', false)
        ?.or('next_service_km.not.is.null,next_service_date.not.is.null');

      if (error) {
        console.warn('[vehicleHistoryService] checkPreventiveAlerts fetch error:', error?.message);
        return;
      }

      const today = new Date();
      const alertThresholdDate = new Date(today);
      alertThresholdDate?.setDate(alertThresholdDate?.getDate() + 7);

      for (const entry of (entries || [])) {
        const vehicleMileage = entry?.vehicle?.mileage || entry?.vehicle?.kilometraje || 0;
        const kmAlert = entry?.next_service_km && vehicleMileage >= entry?.next_service_km;
        const dateAlert = entry?.next_service_date &&
          new Date(entry?.next_service_date) <= alertThresholdDate;

        if (kmAlert || dateAlert) {
          await supabase
            ?.from('vehicle_history')
            ?.update({ alert_ready: true, updated_at: new Date()?.toISOString() })
            ?.eq('id', entry?.id);
        }
      }
    } catch (err) {
      console.warn('[vehicleHistoryService] checkPreventiveAlerts failed:', err?.message);
    }
  },

  /**
   * sendWhatsAppAlertsViaN8N — sends pending alerts to n8n webhook for WhatsApp delivery.
   * Marks alert_sent = true after successful webhook call.
   */
  async sendWhatsAppAlertsViaN8N() {
    if (!N8N_WEBHOOK_URL) {
      console.warn('[vehicleHistoryService] VITE_N8N_WEBHOOK_URL not configured. Skipping webhook.');
      return [];
    }

    try {
      // Fetch entries ready to alert but not yet sent
      const { data: entries, error } = await supabase
        ?.from('vehicle_history')
        ?.select(`
          *,
          vehicle:vehicle_id(
            id, mileage, kilometraje, make, model, marca, modelo, plates, placas, vin,
            client:client_id(id, nombre, telefono, email)
          )
        `)
        ?.eq('type', 'PENDING')
        ?.eq('status', 'OPEN')
        ?.eq('alert_ready', true)
        ?.eq('alert_sent', false);

      if (error) {
        console.warn('[vehicleHistoryService] sendWhatsAppAlertsViaN8N fetch error:', error?.message);
        return [];
      }

      const sent = [];

      for (const entry of (entries || [])) {
        const vehicle = entry?.vehicle;
        const client = vehicle?.client;

        if (!client?.telefono) continue;

        const payload = {
          client_name: client?.nombre || '',
          phone: client?.telefono || '',
          vehicle_make: vehicle?.marca || vehicle?.make || '',
          vehicle_model: vehicle?.modelo || vehicle?.model || '',
          plates: vehicle?.placas || vehicle?.plates || '',
          service_name: entry?.title || '',
          due_km: entry?.next_service_km || null,
          due_date: entry?.next_service_date || null,
        };

        try {
          const response = await fetch(N8N_WEBHOOK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (response?.ok) {
            await supabase
              ?.from('vehicle_history')
              ?.update({ alert_sent: true, updated_at: new Date()?.toISOString() })
              ?.eq('id', entry?.id);
            sent?.push(entry?.id);
          } else {
            console.warn('[vehicleHistoryService] n8n webhook returned non-OK for entry', entry?.id, response?.status);
          }
        } catch (webhookErr) {
          console.warn('[vehicleHistoryService] n8n webhook call failed for entry', entry?.id, webhookErr?.message);
        }
      }

      return sent;
    } catch (err) {
      console.warn('[vehicleHistoryService] sendWhatsAppAlertsViaN8N failed:', err?.message);
      return [];
    }
  },

  /**
   * CRUD for service_rules
   */
  async createServiceRule(ruleData) {
    try {
      const { data, error } = await supabase
        ?.from('service_rules')
        ?.insert(toSnakeCase(ruleData))
        ?.select()
        ?.single();
      if (error) throw new Error(error?.message);
      return toCamelCase(data);
    } catch (err) {
      console.error('[vehicleHistoryService] createServiceRule failed:', err);
      throw err;
    }
  },

  async updateServiceRule(id, ruleData) {
    try {
      const { data, error } = await supabase
        ?.from('service_rules')
        ?.update({ ...toSnakeCase(ruleData), updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();
      if (error) throw new Error(error?.message);
      return toCamelCase(data);
    } catch (err) {
      console.error('[vehicleHistoryService] updateServiceRule failed:', err);
      throw err;
    }
  },
};
